/**
 * SavedPathwayMenus.jsx — iter824 (Feb 2026)
 *
 * Lists the user's saved pathway plans and, for each, computes an
 * on-demand compliance score by comparing the plan foods/tags/DIL/NIS
 * targets against the real 14-day meal_log.
 *
 * Rendered inside the BHI page (below PathwayExplorer) so patients can
 * see:
 *   • which pathway plans they saved
 *   • how well their real eating matched each plan
 *   • which pathway foods they hit or missed
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  BookmarkCheck, Loader2, TrendingUp, ChevronDown, ChevronRight,
  Trash2, Check, X as XIcon, Utensils,
} from "lucide-react";
import { useAuth } from "@/App";

const BAND_ACCENT = {
  "Отлично": { bg: "bg-emerald-100 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-300" },
  "Добро":   { bg: "bg-cyan-100 dark:bg-cyan-500/20",       text: "text-cyan-700 dark:text-cyan-300" },
  "Средно":  { bg: "bg-amber-100 dark:bg-amber-500/20",     text: "text-amber-700 dark:text-amber-300" },
  "Слабо":   { bg: "bg-rose-100 dark:bg-rose-500/20",       text: "text-rose-700 dark:text-rose-300" },
};

export default function SavedPathwayMenus({ isDark = false }) {
  const { API, user } = useAuth();
  const [items, setItems] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [compliance, setCompliance] = useState({}); // { plan_id: {…} }
  const [loadingId, setLoadingId] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/bhi/pathway-menu/saved`);
      setItems(data?.items || []);
    } catch {
      setItems([]);
    }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);

  const fetchCompliance = useCallback(async (planId) => {
    setLoadingId(planId);
    try {
      const { data } = await axios.get(`${API}/bhi/pathway-menu/saved/${planId}/compliance`);
      setCompliance((c) => ({ ...c, [planId]: data }));
    } catch (e) {
      setCompliance((c) => ({ ...c, [planId]: { error: e?.response?.data?.detail || "Грешка." } }));
    } finally {
      setLoadingId(null);
    }
  }, [API]);

  const toggle = (planId) => {
    if (expandedId === planId) { setExpandedId(null); return; }
    setExpandedId(planId);
    if (!compliance[planId]) fetchCompliance(planId);
  };

  const deletePlan = useCallback(async (planId) => {
    if (!window.confirm("Изтриване на записания pathway план?")) return;
    try {
      await axios.delete(`${API}/bhi/pathway-menu/saved/${planId}`);
      setItems((it) => (it || []).filter((x) => x.plan_id !== planId));
    } catch { /* silent */ }
  }, [API]);

  if (!user) return null;
  if (items === null) {
    return null; // still loading first time — silently
  }
  if (items.length === 0) return null;  // hide entirely when nothing saved

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"
  }`;

  return (
    <section
      className={`${cardCls} p-5 mb-4`}
      data-testid="saved-pathway-menus"
    >
      <header className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center shrink-0">
          <BookmarkCheck className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-extrabold bg-gradient-to-r from-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
            🔖 Записани pathway планове
          </h3>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Всеки план може да се сравни с реалния Ви meal log — виждате как реалната диета съвпада с pathway-препоръчаното меню (compliance score).
          </p>
        </div>
      </header>

      <div className="space-y-2" data-testid="saved-pathway-menus-list">
        {items.map((p) => (
          <PlanRow
            key={p.plan_id}
            plan={p}
            isOpen={expandedId === p.plan_id}
            onToggle={() => toggle(p.plan_id)}
            onDelete={() => deletePlan(p.plan_id)}
            compliance={compliance[p.plan_id]}
            loading={loadingId === p.plan_id}
            isDark={isDark}
          />
        ))}
      </div>
    </section>
  );
}

function PlanRow({ plan, isOpen, onToggle, onDelete, compliance, loading, isDark }) {
  const created = plan.created_at ? new Date(plan.created_at).toLocaleDateString("bg-BG") : "";
  const rowCls = `rounded-lg border ${isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-white"}`;
  return (
    <div className={rowCls} data-testid={`saved-plan-${plan.plan_id}`}>
      <div className="flex items-center gap-2 p-2.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          data-testid={`saved-plan-toggle-${plan.plan_id}`}
        >
          {isOpen ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{plan.label}</div>
            <div className="text-[10px] opacity-60">
              {created} · {plan.total_meals} ястия
            </div>
          </div>
        </button>
        {compliance && !compliance.error && (
          <ComplianceBadge value={compliance.composite_pct} band={compliance.band} />
        )}
        <button
          type="button"
          onClick={onDelete}
          title="Изтрий"
          className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-500/20 text-rose-500"
          data-testid={`saved-plan-delete-${plan.plan_id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {isOpen && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-3">
          {loading && (
            <div className="flex items-center gap-2 opacity-70 py-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Изчисляване на compliance…</span>
            </div>
          )}
          {compliance?.error && (
            <div className="text-sm text-red-500">{compliance.error}</div>
          )}
          {compliance && !compliance.error && !loading && (
            <ComplianceView c={compliance} isDark={isDark} />
          )}
        </div>
      )}
    </div>
  );
}

function ComplianceBadge({ value, band }) {
  const s = BAND_ACCENT[band] || BAND_ACCENT["Средно"];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${s.bg} ${s.text}`}
      title={`Compliance: ${value}% (${band})`}
      data-testid="saved-plan-compliance-badge"
    >
      <TrendingUp className="w-3 h-3" />
      {value}% · {band}
    </span>
  );
}

function ComplianceView({ c, isDark }) {
  return (
    <div className="space-y-3" data-testid="saved-plan-compliance">
      {/* Component breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MetricBar label="Съвпадение по храни" value={c.components.food_match_pct} accent="emerald" />
        <MetricBar label="Съвпадение по тагове" value={c.components.tag_match_pct} accent="cyan" />
        <MetricBar label="Близост до DIL цел" value={c.components.dil_close_pct} accent="amber" />
        <MetricBar label="Близост до NIS цел" value={c.components.nis_close_pct} accent="fuchsia" />
      </div>

      {/* Detail row */}
      <div className={`p-2.5 rounded-lg text-[11px] ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Utensils className="w-3 h-3 opacity-60" /> {c.meals_logged} логнати ястия
          </span>
          <span>DIL Ваш: <b>{c.user_dil_avg ?? "—"}</b> / цел <b>{c.target_dil}</b></span>
          <span>NIS Ваш: <b>{c.user_nis_avg ?? "—"}</b> / цел <b>{c.target_nis}</b></span>
        </div>
      </div>

      {/* Matched / Unmatched foods */}
      {(c.matched_foods?.length || c.unmatched_foods?.length) > 0 && (
        <div className="grid md:grid-cols-2 gap-3">
          <FoodChips
            title="Хванати"
            icon={Check}
            items={c.matched_foods}
            accent="emerald"
          />
          <FoodChips
            title="Пропуснати"
            icon={XIcon}
            items={c.unmatched_foods}
            accent="rose"
          />
        </div>
      )}
    </div>
  );
}

function MetricBar({ label, value, accent }) {
  const bgMap = {
    emerald: "bg-emerald-500", cyan: "bg-cyan-500",
    amber:   "bg-amber-500",   fuchsia: "bg-fuchsia-500",
  };
  const textMap = {
    emerald: "text-emerald-700 dark:text-emerald-300",
    cyan:    "text-cyan-700 dark:text-cyan-300",
    amber:   "text-amber-700 dark:text-amber-300",
    fuchsia: "text-fuchsia-700 dark:text-fuchsia-300",
  };
  return (
    <div>
      <div className={`text-[10px] font-bold mb-0.5 ${textMap[accent]}`}>{label}</div>
      <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(0, Math.min(100, value))}%` }}
          transition={{ duration: 0.6 }}
          className={`h-full ${bgMap[accent]}`}
        />
      </div>
      <div className={`text-xs font-extrabold mt-0.5 ${textMap[accent]}`}>{value}%</div>
    </div>
  );
}

function FoodChips({ title, icon: Icon, items, accent }) {
  if (!items || items.length === 0) return null;
  const clsMap = {
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    rose:    "bg-rose-100    text-rose-700    dark:bg-rose-500/20    dark:text-rose-300",
  };
  return (
    <div>
      <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest mb-1 ${clsMap[accent]}`}>
        <Icon className="w-3 h-3" />
        {title} ({items.length})
      </div>
      <div className="flex flex-wrap gap-1">
        {items.slice(0, 15).map((f, i) => (
          <span
            key={i}
            className={`inline-flex px-2 py-0.5 rounded-full text-[11px] ${clsMap[accent]}`}
          >
            {f}
          </span>
        ))}
      </div>
    </div>
  );
}
